import crypto from 'crypto';
import { useFeatureToggles, useSelector } from '@grandvisionhq/state';
import { getAuth } from '../selectors.js';
import { AuthState } from '../state.js';
import { useAccount } from './use-account.js';

/**
 * Hook that provides common user properties for analytics and tracking purposes.
 *
 * This hook returns a promise that resolves with user details including login status
 * and a hashed email (SHA256). The hook leverages the current authentication state
 * to determine whether the user is logged in or a guest. The promise is returned to ensure
 * the `authState` is fully determined (i.e., not `UNKNOWN`) before resolving, providing accurate user data.
 *
 * @returns {Promise<UserDetails>} A promise that resolves with an object containing the user's login status
 * and a SHA256 hash of their email if they are logged in, or an empty string if not.
 *
 * @example
 * const userDetails = useCommonUserProperties()
 * const userData = await userDetails
 * console.log(userData.User_LoginStatus) // 'Logged' or 'Guest'
 * console.log(userData.User_Email_SHA256) // SHA256 hash of email or ''
 */ const useCommonUserProperties = ()=>{
    const authState = useAccount();
    const { isMyAccountEnabled  } = useFeatureToggles();
    const auth = useSelector(getAuth);
    const sha256 = (input)=>crypto.createHash('sha256').update(input).digest('hex')
    ;
    return new Promise((resolve)=>{
        if (isMyAccountEnabled && authState !== 'UNKNOWN') {
            const isUserLoggedIn = authState === AuthState.LoggedIn;
            resolve({
                User_LoginStatus: isUserLoggedIn ? 'Logged' : 'Guest',
                User_Email_SHA256: isUserLoggedIn ? sha256(auth.email) : ''
            });
        }
    });
};

export { useCommonUserProperties };
//# sourceMappingURL=use-common-user-properties.js.map
